import { ref } from "vue"
import { defineStore } from "pinia"
import { UseRootStore } from "@/model/RootStore"
import {
	RegulatoryAssistantFailure,
	RegulatoryAssistantOutcome,
	RegulatoryMessageKind,
	type RegulatoryMessage,
} from "@/types/regulatoryAssistant"
import { analytics } from "@/utils/analytics"
import * as apiClient from "@/utils/ApiClient"

const GENERAL_CONVERSATION_KEY = "general"

export const useRegulatoryAssistantStore = defineStore("regulatoryAssistant", () => {
	const rootStore = UseRootStore()
	const conversationsByKey = ref<Record<string, RegulatoryMessage[]>>({})
	const requestStartedAtByKey = ref<Record<string, number>>({})
	const requestControllersByKey = new Map<string, AbortController>()
	let messageSequence = 0

	function conversationKey(parcelId: string | null): string {
		return parcelId ?? GENERAL_CONVERSATION_KEY
	}

	function nextMessageId(): string {
		messageSequence += 1
		return String(messageSequence)
	}

	function messages(parcelId: string | null): RegulatoryMessage[] {
		return conversationsByKey.value[conversationKey(parcelId)] ?? []
	}

	function isBusy(parcelId: string | null): boolean {
		return conversationKey(parcelId) in requestStartedAtByKey.value
	}

	function requestStartedAt(parcelId: string | null): number | null {
		return requestStartedAtByKey.value[conversationKey(parcelId)] ?? null
	}

	function append(parcelId: string | null, message: RegulatoryMessage): void {
		conversationsByKey.value[conversationKey(parcelId)] = [...messages(parcelId), message]
	}

	function questionCount(parcelId: string | null): number {
		return messages(parcelId).filter(message => message.kind === RegulatoryMessageKind.Question).length
	}

	async function request(parcelId: string | null, question: string, isRetry: boolean): Promise<void> {
		const key = conversationKey(parcelId)
		const controller = new AbortController()
		const startedAt = Date.now()
		requestControllersByKey.set(key, controller)
		requestStartedAtByKey.value[key] = startedAt
		const questionIndex = questionCount(parcelId)

		try {
			const response = await apiClient.askRegulatoryAssistant(
				rootStore.userApiToken,
				question,
				parcelId,
				controller.signal
			)
			const durationMs = Date.now() - startedAt
			append(parcelId, { kind: RegulatoryMessageKind.Answer, id: nextMessageId(), response, durationMs })
			analytics.regulatoryAssistantQuestionAsked({
				question,
				parcelId,
				outcome: RegulatoryAssistantOutcome.Answered,
				confidence: response.confidence,
				durationMs,
				questionIndex,
				isRetry,
			})
		} catch (error) {
			const isStopped = controller.signal.aborted
			if (!isStopped) {
				console.error("Regulatory assistant request failed:", error)
			}
			const durationMs = Date.now() - startedAt
			append(parcelId, {
				kind: RegulatoryMessageKind.Error,
				id: nextMessageId(),
				failure: isStopped ? RegulatoryAssistantFailure.Stopped : RegulatoryAssistantFailure.Failed,
				question,
				durationMs,
			})
			analytics.regulatoryAssistantQuestionAsked({
				question,
				parcelId,
				outcome: isStopped ? RegulatoryAssistantOutcome.Stopped : RegulatoryAssistantOutcome.Failed,
				confidence: null,
				durationMs,
				questionIndex,
				isRetry,
			})
		} finally {
			requestControllersByKey.delete(key)
			delete requestStartedAtByKey.value[key]
		}
	}

	async function ask(parcelId: string | null, question: string): Promise<void> {
		const trimmedQuestion = question.trim()
		if (!trimmedQuestion || isBusy(parcelId)) return

		append(parcelId, { kind: RegulatoryMessageKind.Question, id: nextMessageId(), question: trimmedQuestion })
		await request(parcelId, trimmedQuestion, false)
	}

	async function retry(parcelId: string | null, errorMessageId: string): Promise<void> {
		const errorMessage = messages(parcelId).at(-1)
		if (errorMessage?.id !== errorMessageId || errorMessage.kind !== RegulatoryMessageKind.Error || isBusy(parcelId))
			return

		conversationsByKey.value[conversationKey(parcelId)] = messages(parcelId).filter(
			message => message.id !== errorMessageId
		)
		await request(parcelId, errorMessage.question, true)
	}

	function stop(parcelId: string | null): void {
		requestControllersByKey.get(conversationKey(parcelId))?.abort()
	}

	function clear(parcelId: string | null): void {
		if (isBusy(parcelId)) return
		delete conversationsByKey.value[conversationKey(parcelId)]
	}

	return { messages, isBusy, requestStartedAt, ask, retry, stop, clear }
})
